/** Account and positions: balances, positions, exposure and limits in integer paise (U036-U040). */
import {
  decodeBalanceRow,
  decodeConcentrationRow,
  decodeLimitRow,
  decodePositionRow,
  type BalanceRow,
  type ConcentrationRow,
  type LimitRow,
  type PositionRow,
} from "../../contracts/account.ts";
import {
  badge,
  definitions,
  instantCell,
  metric,
  renderFreshnessBar,
  scrollRegion,
} from "../../app/components.ts";
import { h, setChildren, type Child } from "../../app/dom.ts";
import { formatNullable, formatPaise, formatQuantity } from "../../app/format.ts";
import {
  renderStateBanner,
  stateOfEnvelope,
  stateOfError,
  type ViewState,
} from "../../app/state.ts";
import { anchor, type ServerClock } from "../../app/time.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { CommandSurface } from "../command-surface.ts";
import { handleOf, makeFrame } from "../frame.ts";
import {
  groupExposure,
  markAgeText,
  outcomeLines,
  positionView,
  provenanceCounts,
  utilisationOf,
  type PositionView,
} from "./model.ts";

function paiseOrDash(value: bigint | null): Child {
  return metric(formatNullable(value, formatPaise));
}

interface TableRow {
  readonly cells: readonly Child[];
  readonly attrs: Record<string, string>;
}

const plain = (cells: readonly Child[]): TableRow => ({ cells, attrs: {} });

function table(
  caption: string,
  headers: readonly string[],
  rows: readonly TableRow[],
): HTMLElement {
  return scrollRegion(caption, bareTable(caption, headers, rows));
}

function bareTable(
  caption: string,
  headers: readonly string[],
  rows: readonly TableRow[],
): HTMLElement {
  return h(
    "table",
    { class: "data-table" },
    h("caption", {}, caption),
    h(
      "thead",
      {},
      h("tr", {}, ...headers.map((header) => h("th", { attrs: { scope: "col" } }, header))),
    ),
    h(
      "tbody",
      {},
      ...rows.map((row) =>
        h("tr", { attrs: row.attrs }, ...row.cells.map((cell) => h("td", {}, cell))),
      ),
    ),
  );
}

function balancesSection(balance: BalanceRow): HTMLElement {
  return h(
    "section",
    { attrs: { "aria-labelledby": "account-balances" } },
    h("h2", { attrs: { id: "account-balances" } }, "Balances and capacity"),
    definitions([
      ["Cash", formatPaise(balance.cashPaise)],
      ["Equity", formatPaise(balance.equityPaise)],
      ["Margin in use", formatPaise(balance.marginUsedPaise)],
      ["Collateral", formatPaise(balance.collateralPaise)],
      ["Reserved for open plans and orders", formatPaise(balance.reservedPaise)],
      ["Available capacity", formatPaise(balance.availablePaise)],
      ["Account figures taken", instantCell(balance.accountAt)],
      ["Positions valued", instantCell(balance.valuationAt)],
      ["Account state version", `v${String(balance.accountVersion)}`],
    ]),
    h("h3", {}, "Fees and outcomes"),
    definitions([
      ["Fees, estimated", formatPaise(balance.estimatedFeesPaise)],
      [
        "Fees, reconciled with the broker",
        balance.reconciledFeesPaise === null
          ? "not yet reconciled"
          : formatPaise(balance.reconciledFeesPaise),
      ],
      ...outcomeLines(balance).map(([label, value]) => [label, paiseOrDash(value)] as const),
    ]),
    h(
      "p",
      { class: "note" },
      "Observed and simulated executions are kept in separate lines and are never added together.",
    ),
  );
}

function positionCells(view: PositionView, surface: CommandSurface): Child[] {
  const { row } = view;
  const reduce =
    surface.offered("reduce-position") && row.residualQuantity !== null && row.residualQuantity > 0
      ? surface.launcher({
          intent: "reduce-position",
          scope: `position ${row.instrument}`,
          quantities: `${formatQuantity(row.residualQuantity)} open of ${formatQuantity(row.acquiredQuantity)} acquired`,
          payload: { instrument: row.instrument },
          maxQuantity: row.residualQuantity,
        })
      : null;
  return [
    h("span", { attrs: { "data-instrument": row.instrument } }, row.instrument),
    row.side,
    badge(row.status, row.status === "OPEN" ? "ok" : row.status === "CLOSED" ? "info" : "bad"),
    formatQuantity(row.acquiredQuantity),
    h(
      "span",
      { class: "residual", attrs: { "data-residual": view.residualText } },
      row.residualQuantity === null ? badge("UNKNOWN", "bad") : view.residualText,
    ),
    row.averageEntryPaise === null ? metric(null) : formatPaise(row.averageEntryPaise),
    row.protection === "NOT_APPLICABLE" ? "closed" : row.protection,
    formatPaise(row.realisedPnlPaise),
    paiseOrDash(row.unrealisedPnlPaise),
    row.markPaise === null || row.markAt === null
      ? metric(null)
      : h(
          "span",
          {},
          formatPaise(row.markPaise),
          " (",
          instantCell(row.markAt),
          `; age ${markAgeText(view)})`,
        ),
    badge(
      row.fillState,
      row.fillState === "CONFIRMED" ? "ok" : row.fillState === "PARTIAL" ? "warn" : "bad",
    ),
    row.source === "SIMULATED" ? badge("SIMULATED", "info") : row.source.replace("_", " "),
    h("span", {}, ...view.flags.map((flag) => h("span", { class: "flag" }, `${flag} `))),
    reduce,
  ];
}

function positionsSection(
  rows: readonly PositionRow[],
  clock: ServerClock,
  surface: CommandSurface,
  ctx: ViewContext,
): HTMLElement {
  const views = rows.map((row) => positionView(row, clock, ctx.mono));
  const counts = provenanceCounts(rows);
  return h(
    "section",
    { attrs: { "aria-labelledby": "account-positions" } },
    h("h2", { attrs: { id: "account-positions" } }, "Positions"),
    h(
      "p",
      { class: "note" },
      `Fills by source: ${String(counts.BROKER_REPORTED)} broker-reported, ${String(counts.MANUAL)} manual, ${String(counts.SIMULATED)} simulated. An open, partial or unknown position is never shown as closed.`,
    ),
    h(
      "p",
      {},
      h(
        "a",
        { attrs: { href: "#/portfolio/fills" } },
        "Recorded fills, their provenance and reconciliation",
      ),
    ),
    rows.length === 0
      ? h("p", {}, "No positions for this account.")
      : table(
          "Positions",
          [
            "Instrument",
            "Side",
            "Status",
            "Acquired",
            "Residual",
            "Average entry",
            "Protection",
            "Realised",
            "Unrealised",
            "Mark",
            "Fill state",
            "Source",
            "Notes",
            "Action",
          ],
          views.map((view) => ({
            cells: positionCells(view, surface),
            attrs: { "data-fill-state": view.row.fillState, "data-status": view.row.status },
          })),
        ),
  );
}

function exposureSection(
  rows: readonly ConcentrationRow[],
  limits: readonly LimitRow[],
): HTMLElement {
  const measured = limits.flatMap((limit) =>
    limit.usedPaise === null || limit.limitPaise === null
      ? []
      : [{ limit, used: limit.usedPaise, max: limit.limitPaise }],
  );
  return h(
    "section",
    { attrs: { "aria-labelledby": "account-exposure" } },
    h("h2", { attrs: { id: "account-exposure" } }, "Concentration and risk use"),
    ...groupExposure(rows).map((group) =>
      table(
        `Exposure by ${group.dimension.toLowerCase()}`,
        [
          group.dimension === "NAME"
            ? "Name"
            : group.dimension === "SECTOR"
              ? "Sector"
              : "Catalyst",
          "Exposure",
          "Limit",
          "Use",
          "Positions",
        ],
        group.rows.map((row) => {
          const use = utilisationOf(row.exposurePaise, row.limitPaise);
          return plain([
            row.key,
            formatPaise(row.exposurePaise),
            formatPaise(row.limitPaise),
            h(
              "span",
              { attrs: { "data-over-limit": String(use.over) } },
              use.over ? `${use.text} (over the limit)` : use.text,
            ),
            String(row.positionCount),
          ]);
        }),
      ),
    ),
    table(
      "Daily loss and risk utilisation",
      ["Limit", "Used", "Limit", "Use", "Policy"],
      measured.map(({ limit, used, max }) => {
        const use = utilisationOf(used, max);
        return plain([
          limit.label,
          formatPaise(used),
          formatPaise(max),
          use.over ? `${use.text} (over the limit)` : use.text,
          limit.policyVersion,
        ]);
      }),
    ),
  );
}

/** Mount the account view: balances, positions, exposure, limits. */
export function mountAccount(ctx: ViewContext): ViewHandle {
  const frame = makeFrame("account", "Account and positions");
  if (ctx.accountId === null) {
    frame.banner.append(renderStateBanner({ kind: "no-observations" }));
    return handleOf(frame, ctx);
  }
  frame.banner.append(renderStateBanner({ kind: "loading" }));
  const surface = new CommandSurface(ctx, load);

  async function load(): Promise<void> {
    surface.reset();
    const params = { accountId: ctx.accountId };
    try {
      const [balances, positions, exposure, limits] = await Promise.all([
        ctx.client.read("balances", decodeBalanceRow, params),
        ctx.client.read("positions", decodePositionRow, params),
        ctx.client.read("exposure", decodeConcentrationRow, params),
        ctx.client.read("limits", decodeLimitRow, params),
      ]);
      if (
        balances === null ||
        positions === null ||
        exposure === null ||
        limits === null ||
        ctx.scope.disposed
      ) {
        return;
      }
      const worst: ViewState = [balances, positions, exposure, limits]
        .map(stateOfEnvelope)
        .find((state) => state.kind !== "ready" && state.kind !== "no-observations") ?? {
        kind: "ready",
      };
      setChildren(frame.banner, worst.kind === "ready" ? [] : [renderStateBanner(worst)]);
      setChildren(frame.freshness, [
        renderFreshnessBar(positions, { streamSequence: ctx.streamSequence.get() }),
      ]);
      const [balance] = balances.page.rows;
      surface.clearLaunchers();
      setChildren(frame.body, [
        surface.status,
        balance === undefined
          ? h("p", {}, "No balances reported for this account.")
          : balancesSection(balance),
        positionsSection(
          positions.page.rows,
          anchor(positions.snapshotTime, ctx.mono),
          surface,
          ctx,
        ),
        exposureSection(exposure.page.rows, limits.page.rows),
      ]);
      surface.observe("balances", balances);
      surface.observe("positions", positions);
      surface.observe("exposure", exposure);
      surface.observe("limits", limits);
      surface.identifyFrom(balances);
    } catch (error) {
      setChildren(frame.banner, [renderStateBanner(stateOfError(error))]);
    }
  }

  void load();
  return handleOf(frame, ctx);
}
